import { useCallback, useEffect, useState } from "react";
import { bootstrap, logout, getJson, type ConsoleUser } from "./lib/auth";
import { Login } from "./screens/Login";
import { Enroll } from "./screens/Enroll";
import { Dashboard } from "./screens/Dashboard";
import { ReportsInbox } from "./screens/ReportsInbox";
import { ReportDetail } from "./screens/ReportDetail";
import { theme, fontStack, radius } from "./theme";

type Route = { name: "dashboard" } | { name: "reports" } | { name: "detail"; id: string };

export function App() {
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState<ConsoleUser | null>(null);
  // Le travail d'un modérateur, ce sont les DOSSIERS : la console s'ouvre donc
  // dessus (demande d'Henri). Les statistiques passent en second plan.
  const [route, setRoute] = useState<Route>({ name: "reports" });
  // Enrôlement 2FA en cours (admin sans TOTP) : porte les identifiants du login.
  const [enroll, setEnroll] = useState<{ email: string; password: string } | null>(null);
  // MOD-6 — pastille « Nouveau message » : nombre de dossiers où un utilisateur a
  // répondu sans que personne n'ait encore ouvert le dossier.
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(() => {
    if (!user) return;
    getJson<{ count: number }>("/v1/admin/reports/unread-count")
      .then((r) => setUnread(r.count ?? 0))
      .catch(() => {}); // un compteur d'interface ne doit jamais casser l'écran
  }, [user]);

  // Au démarrage, puis toutes les 60 s : assez pour qu'une réponse ne dorme pas,
  // assez peu pour ne pas marteler le serveur.
  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, 60_000);
    return () => clearInterval(id);
  }, [refreshUnread]);

  useEffect(() => {
    bootstrap()
      .then(setUser)
      .finally(() => setBooting(false));
  }, []);

  const onAuthLost = useCallback(() => setUser(null), []);

  async function handleLogout() {
    await logout();
    setUser(null);
    setRoute({ name: "reports" });
  }

  if (booting) {
    return (
      <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
        Chargement de la console…
      </div>
    );
  }

  if (!user) {
    if (enroll) {
      return (
        <Enroll
          email={enroll.email}
          password={enroll.password}
          // Enrôlement terminé : retour au login, TOTP désormais actif → step-up normal.
          onEnrolled={() => setEnroll(null)}
          onCancel={() => setEnroll(null)}
        />
      );
    }
    return (
      <Login
        onAuthed={(u) => {
          setUser(u);
          setRoute({ name: "dashboard" });
        }}
        onNeedsEnroll={(email, password) => setEnroll({ email, password })}
      />
    );
  }

  return (
    <div style={{ display: "flex", minHeight: "100%" }}>
      <nav
        style={{
          width: 232,
          background: theme.surface,
          borderRight: `1px solid ${theme.line}`,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: fontStack.mono, color: theme.accent, fontSize: 12, letterSpacing: 1 }}>
            LIVEMESSAGE
          </div>
          <div style={{ fontFamily: fontStack.uiSemiBold, fontSize: 16 }}>Console admin</div>
        </div>

        {/* Les dossiers d'abord : c'est le travail. Les chiffres ensuite. */}
        <NavItem
          active={route.name === "reports" || route.name === "detail"}
          onClick={() => setRoute({ name: "reports" })}
          badge={unread}
        >
          Dossiers
        </NavItem>
        <NavItem active={route.name === "dashboard"} onClick={() => setRoute({ name: "dashboard" })}>
          Statistiques
        </NavItem>

        <div style={{ marginTop: "auto", paddingTop: 20, borderTop: `1px solid ${theme.line}` }}>
          <div style={{ fontSize: 13, color: theme.ink2, marginBottom: 4 }}>{user.displayName}</div>
          <div style={{ fontSize: 12, color: theme.ink3, marginBottom: 12, wordBreak: "break-all" }}>{user.email}</div>
          <button
            onClick={handleLogout}
            style={{
              background: "transparent",
              border: `1px solid ${theme.line}`,
              color: theme.ink2,
              borderRadius: radius.pill,
              padding: "7px 14px",
              fontSize: 13,
              width: "100%",
            }}
          >
            Se déconnecter
          </button>
        </div>
      </nav>

      <main style={{ flex: 1, padding: "28px 32px", maxWidth: 1200 }}>
        {route.name === "dashboard" && <Dashboard onAuthLost={onAuthLost} />}
        {route.name === "reports" && (
          <ReportsInbox onOpen={(id) => setRoute({ name: "detail", id })} onAuthLost={onAuthLost} />
        )}
        {route.name === "detail" && (
          <ReportDetail
            reportId={route.id}
            // MOD-6 — revenir à la liste rafraîchit la pastille : le dossier
            // qu'on vient d'ouvrir a été marqué lu côté serveur.
            onBack={() => {
              setRoute({ name: "reports" });
              refreshUnread();
            }}
            onAuthLost={onAuthLost}
          />
        )}
      </main>
    </div>
  );
}

function NavItem({
  active,
  onClick,
  children,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** MOD-6 — nombre de dossiers avec une réponse non lue (0 = rien n'est montré). */
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: "left",
        background: active ? theme.surface3 : "transparent",
        color: active ? theme.ink : theme.ink2,
        border: "none",
        borderRadius: radius.field,
        padding: "10px 12px",
        fontSize: 14,
        fontFamily: active ? fontStack.uiSemiBold : fontStack.ui,
        fontWeight: active ? 600 : 400,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span style={{ flex: 1 }}>{children}</span>
      {badge && badge > 0 ? (
        <span
          title={`${badge} dossier(s) avec un nouveau message`}
          style={{
            background: theme.accent,
            color: theme.onAccent,
            borderRadius: 999,
            minWidth: 20,
            height: 20,
            padding: "0 6px",
            fontSize: 12,
            fontFamily: fontStack.uiSemiBold,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}
